import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { getClients, createClient } from '../services/api';
import { Building2, Plus, Search, Phone, MapPin, X } from 'lucide-react';
import toast from 'react-hot-toast';

const statusColor = (s) =>
  s === 'active' ? 'bg-green-50 text-green-700' :
  s === 'inactive' ? 'bg-gray-50 text-gray-500' :
  'bg-red-50 text-red-600';

const AddClientModal = ({ onClose }) => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    company_name: '', industry: '', city: '',
    state: '', gstin: '',
    credit_period: 30
  });

  const mutation = useMutation({
    mutationFn: createClient,
    onSuccess: () => {
      toast.success('Client created successfully!');
      queryClient.invalidateQueries(['clients']);
      onClose();
    },
    onError: (err) => toast.error(
      err.response?.data?.errors?.[0]?.message ||
      err.response?.data?.message ||
      'Failed to create client'
    )
  });

  const handle = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    mutation.mutate(form);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-semibold text-gray-900">Add New Client</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Company Name *</label>
            <input name="company_name" value={form.company_name} onChange={handle} required
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g. Ashok Leyland Ltd" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Industry</label>
              <input name="industry" value={form.industry} onChange={handle}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g. Manufacturing" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Credit Period (days)</label>
              <input name="credit_period" type="number" value={form.credit_period} onChange={handle}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
              <input name="city" value={form.city} onChange={handle}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g. Chennai" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
              <input name="state" value={form.state} onChange={handle}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g. Tamil Nadu" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
  <             label className="block text-sm font-medium text-gray-700 mb-1">GSTIN</label>
  <             input name="gstin" value={form.gstin} onChange={handle}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="33XXXXX..." />
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" disabled={mutation.isPending}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
              {mutation.isPending ? 'Saving...' : 'Add Client'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const Clients = () => {
  const navigate = useNavigate();
  const [search, setSearch]   = useState('');
  const [showAdd, setShowAdd] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['clients', search],
    queryFn : () => getClients({ search, limit: 20 }).then(r => r.data)
  });

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Clients</h1>
          <p className="text-sm text-gray-400 mt-0.5">Manage your employer companies</p>
        </div>
        <button onClick={() => setShowAdd(true)}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700">
          <Plus size={16} />
          Add Client
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by company name or GSTIN..."
          className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        {isLoading ? (
          <div className="text-center py-12 text-gray-400">Loading clients...</div>
        ) : data?.data?.length === 0 ? (
          <div className="text-center py-12">
            <Building2 size={32} className="mx-auto text-gray-200 mb-3" />
            <p className="text-gray-400 text-sm">No clients yet. Add your first client!</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="text-left text-xs font-medium text-gray-400 px-5 py-3">Company</th>
                <th className="text-left text-xs font-medium text-gray-400 px-5 py-3">Industry</th>
                <th className="text-left text-xs font-medium text-gray-400 px-5 py-3">Location</th>
                <th className="text-left text-xs font-medium text-gray-400 px-5 py-3">Deployments</th>
                <th className="text-left text-xs font-medium text-gray-400 px-5 py-3">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {data?.data?.map(client => (
                <tr key={client.id} onClick={() => navigate(`/clients/${client.id}`)}
                  className="hover:bg-gray-50 transition cursor-pointer">
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                        <span className="text-blue-600 text-xs font-semibold">
                          {client.company_name?.charAt(0)}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{client.company_name}</p>
                        <p className="text-xs text-gray-400">{client.gstin || 'No GSTIN'}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-4 text-sm text-gray-600">{client.industry || '—'}</td>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-1 text-sm text-gray-600">
                      <MapPin size={13} className="text-gray-400" />
                      {client.city || '—'}
                    </div>
                  </td>
                  <td className="px-5 py-4 text-sm text-gray-600">
                    {client._count?.deployments || 0}
                  </td>
                  <td className="px-5 py-4">
                    <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${statusColor(client.status)}`}>
                      {client.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showAdd && <AddClientModal onClose={() => setShowAdd(false)} />}
    </div>
  );
};

export default Clients;